import { NCLEAN_AS_OF } from "./nclean-data";
import type {
  DataBindingDefinition,
  ParameterDefinition,
  ReportTemplateDefinition,
  ReportingDashboard,
  ReportingState,
  TemplateSection,
  WidgetDefinition,
  WidgetInput,
} from "./reporting";

const param = (
  name: string,
  label: string,
  value: string,
): ParameterDefinition => ({
  name,
  label,
  type: ["from", "to", "work_date", "snapshot"].includes(name)
    ? "date"
    : "string",
  default: value,
});
export const BILLING_PARAMETERS = [
  param("from", "From", "2026-10-12"),
  param("to", "To", "2026-11-15"),
];
export const WORK_PARAMETERS = [
  param("work_date", "Work date", "2026-10-17"),
  param("team", "Cleaner or team", "Team A"),
];
export const LOST_PARAMETERS = [
  param("snapshot", "Snapshot date", "2026-05-16"),
];
export const BATCH_PARAMETERS = [
  param("batch", "Handover batch", "NC-2026-05"),
];
const field = (
  name: string,
  type: WidgetInput["type"] = "string",
  nullable = false,
): WidgetInput => ({ name, type, nullable });
const table = (
  id: string,
  name: string,
  inputs: WidgetInput[],
  description = "",
): WidgetDefinition => ({
  id,
  name,
  description,
  revision: 1,
  builtin: true,
  inputs,
  visualization: { type: "table", x: "", y: [], format: "number" },
});
export const NCLEAN_WIDGETS: WidgetDefinition[] = [
  {
    ...table(
      "number",
      "Number",
      [field("value", "number", true)],
      "A numeric input rendered as a KPI.",
    ),
    visualization: { type: "kpi", x: "", y: ["value"], format: "number" },
  },
  {
    ...table("quantity-trend", "Daily recorded quantities", [
      field("service_date", "date"),
      field("recorded_quantity", "number"),
    ]),
    visualization: {
      type: "bar",
      x: "service_date",
      y: ["recorded_quantity"],
      format: "number",
    },
  },
  table("billing-summary", "Company and category totals", [
    field("group_name"),
    field("category"),
    field("payer"),
    field("recorded_quantity", "number"),
    field("allowance_hours", "number"),
  ]),
  {
    ...table(
      "billing-matrix",
      "Property Unit service matrix",
      [
        field("unit_id"),
        field("group_name"),
        field("classification"),
        field("services", "array"),
      ],
      "Weekly paired columns. Blank future cells are unreported; week 42 is treated as submitted.",
    ),
    renderer: "billing-matrix",
  },
  table(
    "allowances",
    "Replenishment allowances",
    [field("group_name"), field("allowance_hours", "number")],
    "Standard allowance in decimal hours; 0.3 hours equals 18 minutes.",
  ),
  table(
    "packs",
    "Bag-packing quantities",
    [
      field("group_name"),
      field("packs4", "number", true),
      field("packs2", "number", true),
    ],
    "Separate 4× and 2× pack counts. An absent source rule remains unavailable.",
  ),
  table("work-log", "Daily work log", [
    field("unit_id"),
    field("vap", "string", true),
    field("cleaner_1"),
    field("cleaner_2", "string", true),
    field("job_start", "timestamp"),
    field("job_finish", "timestamp", true),
    field("elapsed_minutes", "number", true),
    field("notes"),
  ]),
  table("standards", "Standard cleaning durations", [
    field("area_group"),
    field("capacity", "string", true),
    field("standard_minutes", "number"),
    field("effective_from", "date"),
    field("revision"),
  ]),
  {
    ...table("instruction", "Daily instruction", [field("instruction")]),
    localizedInputs: ["instruction"],
  },
  table("register", "Lost-property register", [
    field("id"),
    field("item"),
    field("brand", "string", true),
    field("colour", "string", true),
    field("other_information", "string", true),
    field("found_location"),
    field("recorded_handover_date", "date"),
    field("found_date", "date", true),
  ]),
  table("handover-items", "Handover items", [
    field("item"),
    field("brand", "string", true),
    field("colour", "string", true),
    field("other_information", "string", true),
    field("found_location"),
    field("recorded_handover_date", "date"),
  ]),
];
const billingWhere = "service_date >= {{from}} and service_date <= {{to}}";
const heldWhere =
  "custody_from <= {{snapshot}} and dispatch_date > {{snapshot}}";
const binding = (
  id: string,
  name: string,
  malloy: string,
  parameters: ParameterDefinition[] = [],
  inputMapping: Record<string, string> = {},
): DataBindingDefinition => ({
  id,
  name,
  revision: 1,
  builtin: true,
  query: { mode: "malloy", malloy },
  parameters,
  inputMapping,
});
export const NCLEAN_BINDINGS = [
  binding(
    "billing-count",
    "Recorded billing quantity",
    `run: nclean_billing -> { where: ${billingWhere} aggregate: recorded_quantity }`,
    BILLING_PARAMETERS,
    { value: "recorded_quantity" },
  ),
  binding(
    "billing-trend",
    "Daily quantities",
    `run: nclean_billing -> { where: ${billingWhere} group_by: service_date aggregate: recorded_quantity order_by: service_date }`,
    BILLING_PARAMETERS,
  ),
  binding(
    "billing-summary",
    "Billing summary",
    `run: nclean_billing -> { where: ${billingWhere} group_by: group_name, category, payer aggregate: recorded_quantity, allowance_hours order_by: group_name, category, payer }`,
    BILLING_PARAMETERS,
  ),
  binding(
    "billing-matrix",
    "Complete unit service matrix",
    `run: nclean_units -> {
    group_by: unit_id, group_name, classification
    nest: services is { where: services.service_date >= {{from}} and services.service_date <= {{to}}
      group_by: services.service_date, services.source_column
      aggregate: recorded_quantity is services.recorded_quantity
      nest: evidence is { group_by: services.id, services.source_heading, services.service_marker } }
    order_by: group_name, unit_id
  }`,
    BILLING_PARAMETERS,
  ),
  binding(
    "allowances",
    "Allowance hours by company",
    `run: nclean_billing -> { where: ${billingWhere} group_by: group_name aggregate: allowance_hours order_by: group_name }`,
    BILLING_PARAMETERS,
  ),
  binding(
    "packs",
    "Pack quantities by company",
    `run: nclean_billing -> { where: ${billingWhere} group_by: group_name aggregate: packs4, packs2 order_by: group_name }`,
    BILLING_PARAMETERS,
  ),
  binding(
    "work-log",
    "Dated team work",
    "run: nclean_work_logs -> { where: work_date = {{work_date}} and team = {{team}} group_by: id, unit_id, vap, cleaner_1, cleaner_2, job_start, job_finish, elapsed_minutes, notes order_by: job_start }",
    WORK_PARAMETERS,
  ),
  binding(
    "instruction",
    "Dated daily instruction",
    "run: nclean_instructions -> { where: work_date = {{work_date}} group_by: instruction }",
    [WORK_PARAMETERS[0]],
  ),
  binding(
    "standards",
    "Dated standard reference",
    "run: nclean_standards -> { where: effective_from <= {{work_date}} group_by: display_order, area_group, capacity, standard_minutes, effective_from, revision order_by: display_order }",
    [WORK_PARAMETERS[0]],
  ),
  binding(
    "held-count",
    "Held register entries",
    `run: nclean_lost_entries -> { where: ${heldWhere} aggregate: entry_count }`,
    LOST_PARAMETERS,
    { value: "entry_count" },
  ),
  binding(
    "held-register",
    "Held entries at checkpoint",
    `run: nclean_lost_entries -> { where: ${heldWhere} group_by: id, item, brand, colour, other_information, found_location, recorded_handover_date, found_date order_by: id }`,
    LOST_PARAMETERS,
  ),
  binding(
    "handover-items",
    "Batch membership",
    "run: nclean_lost_entries -> { where: batch_id = {{batch}} group_by: id, item, brand, colour, other_information, found_location, recorded_handover_date order_by: id }",
    BATCH_PARAMETERS,
  ),
  binding(
    "batch-header",
    "Batch header and recipient",
    "run: nclean_batches -> { where: id = {{batch}} group_by: id, property_name, coverage_from, coverage_to, dispatch_date, preparer, recipient, delivery_address, recipient_contact, original_period, original_week }",
    BATCH_PARAMETERS,
  ),
];
const section = (
  widgetId: string,
  bindingId: string,
  title: string,
): TemplateSection => ({
  id: `${bindingId}-section`,
  kind: "widget",
  widgetId,
  bindingId,
  title,
});
const template = (
  id: string,
  name: string,
  description: string,
  parameters: ParameterDefinition[],
  sections: TemplateSection[],
  bindingIds: string[] = [],
): ReportTemplateDefinition => ({
  id,
  name,
  description,
  parameters,
  sections,
  bindingIds,
  revision: 1,
  builtin: true,
  propertyName: "HCR Katinkulta · NClean",
  sourceAt:
    id === "client-verification"
      ? "2026-10-18T23:59:00+03:00"
      : id === "register-snapshot"
        ? "2026-05-18T09:00:00+03:00"
        : id === "lost-handover"
          ? "2026-05-18T09:00:00+03:00"
          : NCLEAN_AS_OF,
});
export const NCLEAN_TEMPLATES: ReportTemplateDefinition[] = [
  template(
    "client-verification",
    "Client Cleaning and Billing Verification",
    "Recorded services and source-based allowances for client review.",
    BILLING_PARAMETERS,
    [
      {
        id: "billing-intro",
        kind: "text",
        title: "Report period",
        text: "HCR Katinkulta · {{params.from}} – {{params.to}}",
      },
      section("number", "billing-count", "Recorded quantities"),
      section(
        "billing-summary",
        "billing-summary",
        "Company and category totals",
      ),
      section(
        "billing-matrix",
        "billing-matrix",
        "Property Unit service matrix",
      ),
      section("allowances", "allowances", "Replenishment allowances"),
      section("packs", "packs", "Bag-packing quantities"),
    ],
  ),
  template(
    "daily-work-log",
    "Daily Cleaner or Team Work Log",
    "Dated work, explanations and the printed duration reference.",
    WORK_PARAMETERS,
    [
      {
        id: "work-header",
        kind: "text",
        title: "Work date and team",
        text: "HCR Katinkulta · {{params.work_date}} · {{params.team}}",
      },
      {
        id: "instruction-text",
        kind: "text",
        title: "Daily instruction",
        text: "{{instruction.instruction}}",
        condition: { bindingId: "instruction", operator: "hasRows" },
      },
      section("work-log", "work-log", "Daily work log"),
      section("standards", "standards", "Standard cleaning durations"),
      {
        id: "no-work",
        kind: "text",
        title: "No recorded work",
        text: "No work records match this date and team.",
        condition: { bindingId: "work-log", operator: "noRows" },
      },
    ],
    ["instruction"],
  ),
  template(
    "register-snapshot",
    "Lost-Property Register Snapshot",
    "A fixed custody register for a selected checkpoint.",
    LOST_PARAMETERS,
    [
      {
        id: "checkpoint",
        kind: "text",
        title: "Snapshot date",
        text: "HCR Katinkulta · {{params.snapshot}}",
      },
      section("number", "held-count", "Register entries"),
      section("register", "held-register", "Lost-property register"),
    ],
  ),
  template(
    "lost-handover",
    "Lost-Property Handover",
    "The selected handover batch and six-column recipient form.",
    BATCH_PARAMETERS,
    [
      {
        id: "recipient",
        kind: "text",
        columns: 2,
        title: "Batch and recipient",
        text: "{{batch-header.property_name}} · {{batch-header.id}}\n{{batch-header.coverage_from}} – {{batch-header.coverage_to}}\nPrepared by {{batch-header.preparer}} · Dispatch {{batch-header.dispatch_date}}\n{{batch-header.recipient}}\n{{batch-header.delivery_address}}\n{{batch-header.recipient_contact}}",
        condition: { bindingId: "batch-header", operator: "hasRows" },
      },
      section("handover-items", "handover-items", "Handover items"),
      {
        id: "missing-batch",
        kind: "text",
        title: "No handover batch",
        text: "No handover batch matches this reference.",
        condition: { bindingId: "batch-header", operator: "noRows" },
      },
    ],
    ["batch-header"],
  ),
];
export const NCLEAN_DASHBOARDS: ReportingDashboard[] = [
  {
    id: "client-verification",
    name: "NClean Client Verification",
    description:
      "Review recorded quantities, allowances and packing by reporting company.",
    parameters: BILLING_PARAMETERS,
    sourceAt: "2026-10-18T23:59:00+03:00",
    builtin: true,
    widgets: [
      ["number", "billing-count", "Recorded quantities", "half"],
      ["quantity-trend", "billing-trend", "Daily recorded quantities", "half"],
      [
        "billing-summary",
        "billing-summary",
        "Company and category totals",
        "full",
      ],
      ["allowances", "allowances", "Replenishment allowances", "half"],
      ["packs", "packs", "Bag-packing quantities", "half"],
      [
        "billing-matrix",
        "billing-matrix",
        "Property Unit service matrix",
        "full",
      ],
    ].map(([widgetId, bindingId, title, width], i) => ({
      id: `billing-${i}`,
      widgetId,
      bindingId,
      title,
      width: width as "third" | "half" | "full",
      ...(widgetId === "number"
        ? { visualization: { showDescription: false } }
        : {}),
    })),
  },
  {
    id: "daily-operations",
    name: "NClean Daily Operations",
    description:
      "Dated cleaner and team records with explanations and reference standards.",
    parameters: WORK_PARAMETERS,
    sourceAt: NCLEAN_AS_OF,
    builtin: true,
    widgets: [
      {
        id: "instruction",
        widgetId: "instruction",
        bindingId: "instruction",
        width: "full",
      },
      {
        id: "work",
        widgetId: "work-log",
        bindingId: "work-log",
        width: "full",
      },
      {
        id: "standards",
        widgetId: "standards",
        bindingId: "standards",
        width: "full",
      },
    ],
  },
  {
    id: "lost-property",
    name: "NClean Lost Property",
    description:
      "Entries held at the selected checkpoint and explicit handover batches.",
    parameters: LOST_PARAMETERS,
    sourceAt: "2026-05-16T18:00:00+03:00",
    builtin: true,
    widgets: [
      {
        id: "held-count",
        widgetId: "number",
        bindingId: "held-count",
        title: "Register entries",
        width: "third",
      },
      {
        id: "held-register",
        widgetId: "register",
        bindingId: "held-register",
        width: "full",
      },
    ],
  },
];
export const defaultReportingState = (): ReportingState =>
  structuredClone({
    version: 3,
    widgets: NCLEAN_WIDGETS,
    bindings: NCLEAN_BINDINGS,
    templates: NCLEAN_TEMPLATES,
    dashboards: NCLEAN_DASHBOARDS,
    deletedBuiltins: [],
  });
